/**
 * pobocka-section-forms — the pobočka form body, one implementation shared by the full-page create
 * surface (`PobockaForm`) and the edit modal (`PobockaEditModal`), like the other modules'
 * `*-section-forms`. A pobočka is a single section, so there is one body.
 *
 * The body owns its fields and local state and renders `<form id={POBOCKA_FORM_ID}>` with fields only;
 * the chrome (Modal.Foot / the create Savebar) supplies the submit button via the HTML `form` attribute.
 *   - modal: `onSubmit` receives the slice, mapped by `pobockaToBody` to a slice-only PUT.
 *   - create: `onChange` mirrors every seed/edit up so the page can assemble the one POST payload.
 */

import type { FormEvent } from 'react';
import { Field, Fields } from '@/components/Field';
import { TextInput } from '@/components/TextInput';
import { useSeededSlice, type BodyProps } from '@/modules/vozidla/vozidlo-section-forms';
import type { PobockaWriteBody, PujcovnaPobocka } from './api';
import { OtviraciDobaFields } from './OtviraciDobaFields';
import { SlugField } from './SlugField';
import { hodinyChyba, hodinySeed, hodinyToBody, type DenRadek } from './otviraci-doba-helpers';

export const POBOCKA_FORM_ID = 'pujcovna-pobocka-form';

export interface PobockaValue {
  nazev: string;
  adresa: string;
  email: string;
  telefon: string;
  /** Public URL key; '' = none. */
  slug: string;
  /** Seven weekday rows, Monday first. */
  hodiny: DenRadek[];
}

export function pobockaSeed(p?: PujcovnaPobocka | null): PobockaValue {
  return {
    nazev: p?.nazev ?? '',
    adresa: p?.adresa ?? '',
    email: p?.email ?? '',
    telefon: p?.telefon ?? '',
    slug: p?.slug ?? '',
    hodiny: hodinySeed(p?.otviraci_doba),
  };
}

/** Trimmed, as the server stores it; `poradi` is not edited here (the list order is a later concern). */
export function pobockaToBody(v: PobockaValue): PobockaWriteBody & { nazev: string } {
  return {
    nazev: v.nazev.trim(),
    adresa: v.adresa.trim(),
    email: v.email.trim(),
    telefon: v.telefon.trim(),
    slug: v.slug.trim(),
    otviraci_doba: hodinyToBody(v.hodiny),
  };
}

export function PobockaFormBody({
  initial,
  busy,
  onSubmit,
  onChange,
  navrhy = [],
}: BodyProps<PobockaValue> & {
  /**
   * Suggestions (create only): clicking one fills the name, and the address when the field is still
   * empty (never over something the user typed); a prefill, not a link.
   */
  navrhy?: Array<{ nazev: string; adresa: string }>;
}) {
  const { form, set, patch } = useSeededSlice(initial, onChange);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy || hodinyChyba(form.hodiny)) return; // the fields show what is wrong
    onSubmit?.(form);
  };

  return (
    <form id={POBOCKA_FORM_ID} onSubmit={submit}>
      <Fields cols={2}>
        <Field label="Název pobočky" required span>
          <TextInput
            value={form.nazev}
            maxLength={100}
            placeholder="Trutnov"
            onChange={(e) => set('nazev', e.currentTarget.value)}
          />
        </Field>
        {navrhy.length > 0 && (
          <div data-testid="pobocka-navrhy" className="col-span-full -mt-2 flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-3">Z poboček LMS</span>
            {navrhy.map((n) => (
              <button
                key={n.nazev}
                type="button"
                className="border border-line px-2.5 py-1 text-[12.5px] text-ink hover:border-ink"
                onClick={() =>
                  patch(form.adresa.trim() === '' && n.adresa ? { nazev: n.nazev, adresa: n.adresa } : { nazev: n.nazev })
                }
              >
                {n.nazev}
              </button>
            ))}
          </div>
        )}
        <Field label="Adresa" span>
          <TextInput
            value={form.adresa}
            maxLength={255}
            placeholder="Ulice a číslo, město"
            onChange={(e) => set('adresa', e.currentTarget.value)}
          />
        </Field>
        <Field label="E-mail" optional="volitelné">
          <TextInput
            type="email"
            value={form.email}
            maxLength={255}
            onChange={(e) => set('email', e.currentTarget.value)}
          />
        </Field>
        <Field label="Telefon" optional="volitelné">
          <TextInput
            type="tel"
            value={form.telefon}
            maxLength={50}
            onChange={(e) => set('telefon', e.currentTarget.value)}
          />
        </Field>
        <SlugField
          value={form.slug}
          onChange={(v) => set('slug', v)}
          nazev={form.nazev}
          placeholder="trutnov"
          hint="Část adresy veřejného webu, např. trutnov. Malá písmena bez diakritiky, číslice, pomlčky; jedinečný napříč firmami."
        />
        <OtviraciDobaFields value={form.hodiny} onChange={(hodiny) => set('hodiny', hodiny)} disabled={busy} />
      </Fields>
    </form>
  );
}
